import moment from "moment";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { IoIosAddCircle } from "react-icons/io";
import RenderToast from "../../Components/atoms/RenderToast/RenderToast";
import SideBarSkeleton from "../../Components/atoms/SideBarSkeleton";
import { LocationModal } from "../../Components/molecules/LocationModal";
import PopOver from "../../Components/molecules/PopOver/PopOver";
import SectionHeader from "../../Components/molecules/SectionHeader";
import Table from "../../Components/organisms/Table";
import { RECORDS_LIMIT } from "../../developmentContent/constants";
import { Delete, Get, Patch, Post } from "../../interceptor/axiosInterceptor";
import useDebounce from "../../resources/customHooks/useDebounce";
import { capitalizeFirstLetter, CreateFormData } from "../../resources/utils/helper";
import classes from "./Locations.module.css";

export default function Locations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const debounceSearch = useDebounce(search);

  const onClickPopover = async (label = "", dataItem = null) => {
    if (label === "Delete") {
      if (window.confirm(`Are you sure you want to delete the location page "${dataItem?.title}"?`)) {
        setLoading("deleting");
        const { response } = await Delete({
          route: `admin/location/${dataItem?.slug}`,
        });
        if (response) {
          RenderToast({ type: "success", message: "Location deleted successfully!" });
          getLocationsData({ pg: page, _search: debounceSearch });
        }
        setLoading("");
      }
      return;
    }

    setCurrentLocation(dataItem);
    setShowModal(label === "View" ? "viewModal" : "editModal");
  };

  const LOCATION_POPOVER = [{ label: "Edit" }, { label: "Delete" }];

  const tableHeader = [
    { id: "title", label: "Page Title", style: { width: "25%" } },
    { id: "slug", label: "SEO URL Slug", style: { width: "25%" } },
    { id: "county", label: "County", style: { width: "15%" } },
    { id: "city", label: "City", style: { width: "15%" } },
    { id: "status", label: "Status", style: { width: "10%" } },
    {
      id: "action",
      label: "Action",
      style: { width: "10%", textAlign: "center" },
      renderValue: (item) => {
        const itemData = data?.find((e) => e?.slug === item?.id || e?._id === item?.id);
        return (
          <PopOver
            popover={LOCATION_POPOVER}
            onClick={(label) => onClickPopover(label, itemData)}
          />
        );
      },
    },
  ];

  const tableBody = data?.map((item) => ({
    title: capitalizeFirstLetter(item?.title),
    slug: `/location/${item?.slug}`,
    county: item?.county,
    city: item?.city,
    status: item?.isActive ? "Active" : "Inactive",
    id: item?.slug,
  }));

  const getLocationsData = async ({ pg = page, _search = debounceSearch }) => {
    setLoading("loadings");
    const params = {
      page: pg,
      search: _search,
      limit: RECORDS_LIMIT,
    };

    const queryParams = new URLSearchParams(params)?.toString();
    const url = "admin/location/all";

    const { response } = await Get({
      route: `${url}?${queryParams}`,
    });

    if (response) {
      setData(response?.data?.locations || []);
      setPage(pg);
      setTotalRecords(response?.data?.totalCount || 0);
    }
    setLoading("");
  };

  const handleSubmit = async (values) => {
    const payload = {
      title: values?.title,
      subTitle: values?.subTitle,
      county: values?.county,
      city: values?.city,
      slug: values?.slug,
      description: values?.description,
      metaTitle: values?.metaTitle,
      metaDescription: values?.metaDescription,
      metaKeywords: values?.metaKeywords,
      ...(values?.image && typeof values.image !== "string" && { image: values?.image }),
      isActive: values?.status?.value ?? true,
    };

    setLoading("postingLocation");

    const route = currentLocation?.slug
      ? `admin/location/${currentLocation?.slug}`
      : "admin/location";
    const apiMethod = currentLocation?.slug ? Patch : Post;
    const formData = CreateFormData(payload);

    const { response } = await apiMethod({
      route,
      data: formData,
      isFormData: true,
    });

    if (response) {
      RenderToast({
        type: "success",
        message: `Location ${currentLocation?.slug ? "Updated" : "Added"} Successfully!`,
      });
      getLocationsData({ pg: page, _search: debounceSearch });
      setCurrentLocation(null);
      setShowModal("");
    }

    setLoading("");
  };

  useEffect(() => {
    setPage(1);
    getLocationsData({ pg: 1, _search: debounceSearch });
  }, [debounceSearch]);

  return (
    <SideBarSkeleton>
      <Container fluid>
        <Row>
          <Col md={12}>
            <SectionHeader
              title="Location Pages (SEO)"
              showButton
              showSearch
              search={search}
              setSearch={setSearch}
              label="Add Location"
              btnClass={classes.btnClass}
              leftIcon={<IoIosAddCircle size={20} />}
              variant="primary"
              onClick={() => {
                setCurrentLocation(null);
                setShowModal("addModal");
              }}
            />
          </Col>
          <Col md={12}>
            <div className={classes.tableDiv}>
              <Table
                currentPage={page}
                totalRecords={totalRecords}
                hasPagination
                headings={tableHeader}
                tableData={tableBody}
                loading={loading === "loadings"}
                setCurrentPage={(p) => {
                  setPage(p);
                  getLocationsData({ pg: p, _search: debounceSearch });
                }}
              />
            </div>
          </Col>
        </Row>
      </Container>

      {["addModal", "editModal"].includes(showModal) && (
        <LocationModal
          showModal={["addModal", "editModal"].includes(showModal)}
          setShowModal={() => {
            setCurrentLocation(null);
            setShowModal("");
          }}
          loading={loading === "postingLocation"}
          data={currentLocation}
          handleSubmit={handleSubmit}
        />
      )}
    </SideBarSkeleton>
  );
}
